import {getReporterAssessmentView, getReporterAssessmentResultView, normalizeReporterAssessmentConfig, getReporterAssessmentConfigErrors,
	addReporterAssessmentRole, duplicateReporterAssessmentRole, removeReporterAssessmentRole, addReporterAssessmentDomain,
	removeReporterAssessmentDomain, addReporterAssessmentQuestion, removeReporterAssessmentQuestion, moveReporterAssessmentQuestion,
	addReporterAssessmentScaleOption, removeReporterAssessmentScaleOption} from "./reporterAssessmentHelpers.js";
import {getFormCapabilityConfigErrors, getFormCapabilityPayloadErrors} from "./formValidationCapabilities.js";
import {formatFormResultDate, getNarrativeFormResultView, getAnchoredFormRatingReview} from "./formResultHelpers.js";
import {normalizePositionMapConfig, getPositionMapSections, getPositionMapCompletion, getMostRecentPositionResult, getPositionMapResultView} from "./positionMapHelpers.js";
import {isStrictFormSubmission, validateFormWorkInput, createFixedPromptSnapshot, validateReporterAssessmentPayload, validateStructuredAssessmentPayload} from "./formWorkLifecycleHelpers";
import type {ToolWorkContext, ToolParticipation, ToolParticipationPolicy} from "./ToolWorkLifecycleTypes";
import {createFormSummaryInput, getStructuredFormView} from "./formValidationHelpers.js";
import {getAnchoredMeanReport, getAnchoredMeanResultView} from "./anchoredMeanHelpers.js";
import {getFormScriptView, updateFormScriptSelection, updateFormScriptReady, createFormScriptSummary} from "./formScriptHelpers.js";
import {normalizeHandoutConfig, getHandoutView, updateHandoutReady, editHandoutCollection, getHandoutSummary, reconcileHandoutAcknowledgement} from "./handoutHelpers.js";
import {getFrequencyInventoryReport, getFrequencyInventoryResultView} from "./frequencyInventoryHelpers.js";
import {getSubscaleProfileResult, getSubscaleProfileResultView} from "./subscaleProfileHelpers.js";
import {getPairedQuestionnaireReport, getPairedQuestionnaireResultView, updatePairedQuestionnaireAnswer, updatePairedQuestionnaireName, markPairedQuestionnaireAdapted, addPairedQuestionnaireSection, removePairedQuestionnaireSection, movePairedQuestionnaireSection, addPairedQuestionnaireQuestion, removePairedQuestionnaireQuestion, movePairedQuestionnaireQuestion, movePairedQuestionnaireQuestionToSection, addPairedQuestionnaireOption, removePairedQuestionnaireOption, movePairedQuestionnaireOption, setPairedQuestionnaireRatingMaximum, setPairedQuestionnaireResponseKind} from "./pairedQuestionnaireHelpers.js";
import {updateFormChoiceList, updateFormDiscreteRating, getFormDiscreteRatingReview} from "./discreteRatingHelpers.js";
import {createOrdinalProfileConfig, createOrdinalProfileSummary, getArchivedOrdinalProfileItems, getOrdinalProfileStatus, getOrdinalProfileComparisons, getOrdinalProfileDetails} from "./ordinalProfileHelpers.js";
import {plainToClass} from "@qwoach/class-transformer-wrapper";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import FormItem from "@/core/modules/coach/Resources/Forms/model/FormItem.js";
import type {
	EnginePayload,
	FormSchemaEngineInterface,
	FormResponseEngineInterface,
	ToolConfig,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	FormSchemaEngineInterfaceToken,
	FormResponseEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	ToolEngineOptions,
	ToolEngineValidationResult,
	type FormModelInterface,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

export default class FormToolEngine extends BaseToolEngine implements FormSchemaEngineInterface, FormResponseEngineInterface {
	markPairedQuestionnaireAdapted(config: ToolConfig = {}): ToolConfig { return markPairedQuestionnaireAdapted(config) }
	addPairedQuestionnaireSection(config: ToolConfig = {}): ToolConfig { return addPairedQuestionnaireSection(config) }
	removePairedQuestionnaireSection(config: ToolConfig = {}, sectionId: string = ""): ToolConfig { return removePairedQuestionnaireSection(config, sectionId) }
	movePairedQuestionnaireSection(config: ToolConfig = {}, index: number = 0, offset: number = 0): ToolConfig { return movePairedQuestionnaireSection(config, index, offset) }
	addPairedQuestionnaireQuestion(config: ToolConfig = {}, sectionId: string = ""): ToolConfig { return addPairedQuestionnaireQuestion(config, sectionId) }
	removePairedQuestionnaireQuestion(config: ToolConfig = {}, sectionId: string = "", questionId: string = ""): ToolConfig { return removePairedQuestionnaireQuestion(config, sectionId, questionId) }
	movePairedQuestionnaireQuestion(config: ToolConfig = {}, sectionId: string = "", index: number = 0, offset: number = 0): ToolConfig { return movePairedQuestionnaireQuestion(config, sectionId, index, offset) }
	movePairedQuestionnaireQuestionToSection(config: ToolConfig = {}, sectionId: string = "", questionId: string = "", destinationId: string = ""): ToolConfig { return movePairedQuestionnaireQuestionToSection(config, sectionId, questionId, destinationId) }
	addPairedQuestionnaireOption(config: ToolConfig = {}): ToolConfig { return addPairedQuestionnaireOption(config) }
	removePairedQuestionnaireOption(config: ToolConfig = {}, optionId: string = ""): ToolConfig { return removePairedQuestionnaireOption(config, optionId) }
	movePairedQuestionnaireOption(config: ToolConfig = {}, index: number = 0, offset: number = 0): ToolConfig { return movePairedQuestionnaireOption(config, index, offset) }
	setPairedQuestionnaireRatingMaximum(config: ToolConfig = {}, maximum: number = 5): ToolConfig { return setPairedQuestionnaireRatingMaximum(config, maximum) }
	setPairedQuestionnaireResponseKind(config: ToolConfig = {}, kind: string = "choice"): ToolConfig { return setPairedQuestionnaireResponseKind(config, kind) }
	getPairedQuestionnaireReport(config: ToolConfig = {}, reports: ToolConfig = {}): ToolConfig { return getPairedQuestionnaireReport(config, reports) }
	getPairedQuestionnaireResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getPairedQuestionnaireResultView(config, result, priorResults) }
	updatePairedQuestionnaireAnswer(payload: EnginePayload = {}, config: ToolConfig = {}, partnerId: string = "", questionId: string = "", optionId: string | null = null): EnginePayload { return updatePairedQuestionnaireAnswer(payload, config, partnerId, questionId, optionId) }
	updatePairedQuestionnaireName(payload: EnginePayload = {}, config: ToolConfig = {}, partnerId: string = "", name: string = ""): EnginePayload { return updatePairedQuestionnaireName(payload, config, partnerId, name) }
	getParticipation(config: ToolConfig = {}): ToolParticipation {
		if (config.reporterAssessment && !config.pairedQuestionnaire) {
			const view = getReporterAssessmentView(config)
			const forms = view.forms as ToolConfig
			const reporters = Object.keys(forms)
			return {isPrivate: false, participants: reporters.map(id => ({id, title: String((forms[id] as ToolConfig).label || id)}))}
		}
		const questionnaire = config.pairedQuestionnaire as ToolConfig | undefined
		const partners = Array.isArray(questionnaire?.partners) ? questionnaire.partners as ToolConfig[] : []
		return {isPrivate: !!questionnaire, participants: partners.map(partner => ({id: String(partner.id), title: String(partner.title || partner.id)}))}
	}

	getParticipationPolicy(config: ToolConfig = {}, definition: ToolWorkContext["definition"] = null): ToolParticipationPolicy {
		if (!config.reporterAssessment && !config.pairedQuestionnaire) return super.getParticipationPolicy(config, definition)
		const configuredRoles = super.getParticipationPolicy(config, definition).roles
		const participants = this.getParticipation(config).participants
		return {roles: participants.map(participant => {
			const role = configuredRoles.find(item => item.id === participant.id) || {
				...this.createParticipationRole(participant.id, participant.title),
				allowsMultipleAssignments: !config.pairedQuestionnaire,
			}
			return {...role, title: participant.title, capabilities: {...role.capabilities}}
		})}
	}

	getParticipationConfig(config: ToolConfig, participantId: string): ToolConfig {
		if (config.reporterAssessment && !config.pairedQuestionnaire) {
			const assessment = config.reporterAssessment as ToolConfig
			const forms = assessment.forms as ToolConfig | undefined
			return forms?.[participantId] ? {...JSON.parse(JSON.stringify(config)), reporterSelection: participantId} : config
		}
		if (!config.pairedQuestionnaire) return config
		if (!this.getParticipation(config).participants.some(partner => partner.id === participantId)) throw new Error("Choose a partner-specific questionnaire link")
		return {...JSON.parse(JSON.stringify(config)), selectedPartnerId: participantId}
	}

	getRoleConfig(config: ToolConfig, roleId: string, definition: ToolWorkContext["definition"] = null): ToolConfig {
		super.getRoleConfig(config, roleId, definition)
		return this.getParticipationConfig(config, roleId)
	}

	selectParticipationPayload(payload: EnginePayload, config: ToolConfig, participantId: string): EnginePayload {
		if (config.reporterAssessment && config.reporterSelection === participantId && participantId) {
			this.getRoleConfig(config, participantId)
			if (Object.keys(payload.reports || {}).some(id => id !== participantId)) throw new Error("A reporter response cannot include another reporter's answers")
			return {...payload, form: JSON.parse(JSON.stringify(config))}
		}
		if (!config.pairedQuestionnaire) return payload
		this.getParticipationConfig(config, participantId)
		if (Object.keys(payload.reports || {}).some(id => id !== participantId)) throw new Error("A partner response cannot include another partner's answers")
		const selected: EnginePayload = {...payload, form: JSON.parse(JSON.stringify(config))}
		delete selected.questionnaireReport
		return selected
	}

	canAccessPublicResult(config: ToolConfig = {}): boolean { return !config.pairedQuestionnaire && config.visibility !== "practitioner" && super.canAccessPublicResult(config) }
	getHandoutView(config: ToolConfig = {}, payload: EnginePayload = {}): ToolConfig { return getHandoutView(config, payload) }
	updateHandoutReady(config: ToolConfig = {}, ready = false): ToolConfig { return updateHandoutReady(config, ready) }
	editHandoutCollection(config: ToolConfig = {}, kind = "sections", operation = "add", index = 0, sectionId = ""): ToolConfig { return editHandoutCollection(config, kind, operation, index, sectionId) }
	updateFormChoiceList(config: ToolConfig = {}, listName: string = "", fieldId: string = "", operation: string = "", index: number = 0, value: ToolConfig = {}): ToolConfig { return updateFormChoiceList(config, listName, fieldId, operation, index, value) }
	updateFormDiscreteRating(payload: EnginePayload = {}, config: ToolConfig = {}, choiceId: string | null = null, status: string = "unanswered"): EnginePayload { return updateFormDiscreteRating(payload, config, choiceId, status) }
	getFormDiscreteRatingReview(result: ToolConfig = {}, priorResults: ToolConfig[] = [], fallbackConfig?: ToolConfig): ToolConfig { return getFormDiscreteRatingReview(result, priorResults, fallbackConfig) }
	getAnchoredMeanReport(config: ToolConfig = {}, answers: ToolConfig = {}): ToolConfig { return getAnchoredMeanReport(config, answers) }
	getAnchoredMeanResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getAnchoredMeanResultView(config, result, priorResults) }

	formatFormResultDate(value: unknown): string { return formatFormResultDate(value) }
	normalizeReporterAssessmentConfig(config: ToolConfig = {}): ToolConfig { return normalizeReporterAssessmentConfig(config) }
	getReporterAssessmentConfigErrors(config: ToolConfig = {}): string[] { return getReporterAssessmentConfigErrors(config) }
	addReporterAssessmentRole(config: ToolConfig, roleId: string, questionId: string): ToolConfig { return addReporterAssessmentRole(config, roleId, questionId) }
	duplicateReporterAssessmentRole(config: ToolConfig, sourceRoleId: string, newRoleId: string, createId: (kind: string) => string): ToolConfig { return duplicateReporterAssessmentRole(config, sourceRoleId, newRoleId, createId) }
	removeReporterAssessmentRole(config: ToolConfig, roleId: string): ToolConfig { return removeReporterAssessmentRole(config, roleId) }
	addReporterAssessmentDomain(config: ToolConfig, roleId: string, domainId: string): ToolConfig { return addReporterAssessmentDomain(config, roleId, domainId) }
	removeReporterAssessmentDomain(config: ToolConfig, roleId: string, domainId: string): ToolConfig { return removeReporterAssessmentDomain(config, roleId, domainId) }
	addReporterAssessmentQuestion(config: ToolConfig, roleId: string, questionId: string): ToolConfig { return addReporterAssessmentQuestion(config, roleId, questionId) }
	removeReporterAssessmentQuestion(config: ToolConfig, roleId: string, questionId: string): ToolConfig { return removeReporterAssessmentQuestion(config, roleId, questionId) }
	moveReporterAssessmentQuestion(config: ToolConfig, roleId: string, index: number, offset: number): ToolConfig { return moveReporterAssessmentQuestion(config, roleId, index, offset) }
	addReporterAssessmentScaleOption(config: ToolConfig): ToolConfig { return addReporterAssessmentScaleOption(config) }
	removeReporterAssessmentScaleOption(config: ToolConfig, index: number): ToolConfig { return removeReporterAssessmentScaleOption(config, index) }
	getReporterAssessmentView(config: ToolConfig = {}, payload: EnginePayload = {}, reporterId: string = ""): ToolConfig { return getReporterAssessmentView(config, payload, reporterId) }
	getReporterAssessmentResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getReporterAssessmentResultView(config, result, priorResults) }
	getFormResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig {
		return getNarrativeFormResultView(config, result, priorResults, value => value)
	}
	getAnchoredFormRatingReview(result: ToolConfig = {}, priorResults: ToolConfig[] = [], fallbackConfig?: ToolConfig): ToolConfig { return getAnchoredFormRatingReview(result, priorResults, fallbackConfig) }

	getPositionMapResultView(config?: ToolConfig | null, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getPositionMapResultView(config, result, priorResults) }
	getReflectiveStatementResultView(config?: ToolConfig | null, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig {
		return getNarrativeFormResultView(config, result, priorResults, value => this.normalizeConfig(value))
	}


	normalizePositionMapConfig(config: ToolConfig = {}): ToolConfig { return normalizePositionMapConfig(config) }
	getPositionMapSections(config: ToolConfig = {}): ToolConfig[] { return getPositionMapSections(config) }
	getPositionMapCompletion(config: ToolConfig = {}, answers: ToolConfig = {}): ToolConfig { return getPositionMapCompletion(config, answers) }
	getMostRecentPositionResult(results: ToolConfig[] = []): ToolConfig | null { return getMostRecentPositionResult(results) }
	getFrequencyInventoryReport(inventory: ToolConfig = {}, answers: ToolConfig = {}): ToolConfig { return getFrequencyInventoryReport(inventory, answers) }
	getSubscaleProfileResult(answers: ToolConfig = {}, config: ToolConfig = {}): ToolConfig { return getSubscaleProfileResult(answers, config) }
	getOrdinalProfileStatus(config: ToolConfig = {}, answers: ToolConfig = {}): ToolConfig { return getOrdinalProfileStatus(config, answers) }
	getOrdinalProfileDetails(config: ToolConfig = {}, answers: ToolConfig = {}, includeEmpty: boolean = false): ToolConfig[] { return getOrdinalProfileDetails(config, answers, includeEmpty) }
	getOrdinalProfileComparisons(config: ToolConfig = {}, results: ToolConfig[] = []): ToolConfig[] { return getOrdinalProfileComparisons(config, results) }
	getArchivedOrdinalProfileItems(payload: EnginePayload = {}, config: ToolConfig = {}): ToolConfig[] { return getArchivedOrdinalProfileItems(payload, config) }
	getFrequencyInventoryResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getFrequencyInventoryResultView(config, result, priorResults) }
	getSubscaleProfileResultView(config?: ToolConfig, result: ToolConfig = {}, priorResults: ToolConfig[] = []): ToolConfig { return getSubscaleProfileResultView(config, result, priorResults) }

	isWorkNormalizationStrict(): boolean { return false }
	getWorkNormalizationScope(): string { return "formNormalization" }
	canAccessPublicWork(config: ToolConfig = {}, isOwner = false): boolean {
		return (config.visibility !== "practitioner" || isOwner) && (!config.handoutConfig || isOwner || getHandoutView(config).isReady === true)
	}

	requiresPreviousWorkPayload(config: ToolConfig = {}): boolean {
		return !!config?.fixedPromptSequence
	}

	validateWorkInput(payload: EnginePayload, config: ToolConfig): void {
		if (isStrictFormSubmission(config)) validateFormWorkInput(payload, config)
	}

	prepareWorkInput(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext, isSubmission: boolean): EnginePayload {
		if (config.pairedQuestionnaire) payload = this.selectParticipationPayload(payload, config, String(context.participantId || ""))
		if (config.handoutConfig) {
			payload = reconcileHandoutAcknowledgement(config, payload)
			const handout = config.handoutConfig as ToolConfig
			payload.context = handout.context || ""
			payload.selectedFocusId = handout.defaultFocusId || ""
		}
		if (config.discreteRating) payload.form = JSON.parse(JSON.stringify(this.normalizeConfig(config)))
		if (config.fixedPromptSequence) payload.form = createFixedPromptSnapshot(config, context.previousPayload?.form as ToolConfig)
		if (config.frequencyInventory) {
			payload.form = JSON.parse(JSON.stringify(config))
			if (isSubmission) payload.completedDate = context.submissionDate
		}
		return payload
	}

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig): EnginePayload {
		return this.normalizePayload(payload, config)
	}

	finalizeWorkSubmission(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext): EnginePayload {
		if (config.pairedQuestionnaire) this.assertWorkValidation(this.validateConfig(config))
		if (config.handoutConfig) {
			const view = getHandoutView(config, payload)
			const missing = Array.isArray(view.missing) ? view.missing : []
			if (!view.isReady) throw new Error(missing.length ? `Complete ${missing.join(", ")} before sharing` : "Mark the handout ready before sharing")
			payload.form = JSON.parse(JSON.stringify(this.normalizeConfig(config)))
			if (!(view.handout as ToolConfig)?.acknowledgementEnabled) {
				payload.acknowledged = false
				payload.acknowledgedAt = ""
			}
		}
		if (config.discreteRating) payload.administrationDate = context.submissionDate
		if (config.scriptConfig) payload.form = JSON.parse(JSON.stringify(this.normalizeConfig(config)))
		if (config.validateFormAnswers && !config.fixedPromptSequence) payload.form = JSON.parse(JSON.stringify(config))
		if (config.validateFormAnswers || config.requireAnyTextAnswer || config.subscaleProfile || config.pairedQuestionnaire || config.frequencyInventory) this.assertWorkValidation(this.validateWorkPayload(payload, config))
		if (config.pairedQuestionnaire) {
			payload.form = JSON.parse(JSON.stringify(config))
			payload.questionnaireReport = getPairedQuestionnaireReport(config, payload.reports as ToolConfig)
		}
		if (config.subscaleProfile) {
			payload = this.createSubscaleProfileSubmission(payload, config)
			payload.administrationDate = context.submissionDate
		}
		if (config.assessment) validateStructuredAssessmentPayload(payload, config)
		if (config.reporterAssessment) validateReporterAssessmentPayload(payload, config)
		return payload
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig): ToolEngineValidationResult {
		return this.collectWorkValidation(() => {
			payload = JSON.parse(JSON.stringify(payload || {}))
			config = JSON.parse(JSON.stringify(config || {}))
			if (config.reporterAssessment) validateReporterAssessmentPayload(payload, config)
			else {
				this.assertWorkValidation(this.validateFormPayload(payload, config))
				if (config.assessment) validateStructuredAssessmentPayload(payload, config)
			}
		})
	}

	getEngineInterfaceTokens() {
		return [
			FormSchemaEngineInterfaceToken,
			FormResponseEngineInterfaceToken,
		]
	}

	normalizeConfig(config: ToolConfig = {}): ToolConfig {
		if (config.handoutConfig) return normalizeHandoutConfig(config)
		if (config.ordinalProfile) return createOrdinalProfileConfig(config)
		return {...config, fields: config.fields || config.formElements || []}
	}

	getFormScriptView(config: ToolConfig = {}, payload: EnginePayload = {}): ToolConfig { return getFormScriptView(config, payload) }
	updateFormScriptSelection(payload: EnginePayload = {}, config: ToolConfig = {}, optionId: string = "", selected: boolean = false): EnginePayload { return updateFormScriptSelection(payload, config, optionId, selected) }
	updateFormScriptReady(payload: EnginePayload = {}, config: ToolConfig = {}, isReady: boolean = false): EnginePayload { return updateFormScriptReady(payload, config, isReady) }
	addFormScriptOption(config: ToolConfig = {}, option: ToolConfig = {}): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		const options = (scriptConfig.ruleOptions || []) as ToolConfig[]
		return {...config, scriptConfig: {...scriptConfig, ruleOptions: [...options, {id: option.id || `rule_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, title: option.title || "New response-prevention rule"}]}}
	}
	removeFormScriptOption(config: ToolConfig = {}, optionId: string = ""): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		return {...config, scriptConfig: {...scriptConfig, ruleOptions: ((scriptConfig.ruleOptions || []) as ToolConfig[]).filter(option => option.id !== optionId)}}
	}
	moveFormScriptOption(config: ToolConfig = {}, index: number = 0, offset: number = 0): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		const options = [...((scriptConfig.ruleOptions || []) as ToolConfig[])]
		const destination = index + offset
		if (index < 0 || destination < 0 || index >= options.length || destination >= options.length) return config
		options.splice(destination, 0, options.splice(index, 1)[0])
		return {...config, scriptConfig: {...scriptConfig, ruleOptions: options}}
	}
	addFormScriptMode(config: ToolConfig = {}): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		const modes = (scriptConfig.modes || []) as ToolConfig[]
		return {...config, scriptConfig: {...scriptConfig, modes: [...modes, {id: `mode_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, title: "New use mode", instructions: ""}]}}
	}
	removeFormScriptMode(config: ToolConfig = {}, modeId: string = ""): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		const modes = ((scriptConfig.modes || []) as ToolConfig[]).filter(mode => mode.id !== modeId)
		return {...config, scriptConfig: {...scriptConfig, modes, modeId: scriptConfig.modeId === modeId ? modes[0]?.id || "" : scriptConfig.modeId}}
	}
	moveFormScriptMode(config: ToolConfig = {}, index: number = 0, offset: number = 0): ToolConfig {
		const scriptConfig = (config.scriptConfig || {}) as ToolConfig
		const modes = [...((scriptConfig.modes || []) as ToolConfig[])]
		const destination = index + offset
		if (index < 0 || destination < 0 || index >= modes.length || destination >= modes.length) return config
		modes.splice(destination, 0, modes.splice(index, 1)[0])
		return {...config, scriptConfig: {...scriptConfig, modes}}
	}

	addFormSection(config: ToolConfig = {}, section: ToolConfig = {}): ToolConfig {
		const sections = Array.isArray(config.sections) ? config.sections : []
		const next = {...section, id: section.id || `section_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`}
		return {...config, sections: [...sections, next]}
	}

	removeFormSection(config: ToolConfig = {}, sectionId: string): ToolConfig {
		const sections = (Array.isArray(config.sections) ? config.sections : []) as ToolConfig[]
		const remaining = sections.filter(section => section.id !== sectionId)
		const fallback = remaining[0]?.id || ""
		const fields = ((Array.isArray(config.fields) ? config.fields : []) as ToolConfig[]).map(field => ({
			...field,
			...(field.section === sectionId ? {section: fallback} : {}),
			...(field.sectionId === sectionId ? {sectionId: fallback} : {}),
		}))
		return {...config, sections: remaining, fields}
	}

	addFormField(config: ToolConfig = {}, field: ToolConfig = {}): ToolConfig {
		const fields = Array.isArray(config.fields) ? config.fields : []
		const next = {...field, id: field.id || `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`}
		return {...config, fields: [...fields, next]}
	}

	addFormRepeatRow(config: ToolConfig = {}, groupId: string): ToolConfig {
		const group = ((config.minimumGroups || []) as ToolConfig[]).find(item => item.id === groupId)
		if (!group) return config
		const fields = (Array.isArray(config.fields) ? config.fields : []) as ToolConfig[]
		const prefix = String(group.prefix)
		const matches = fields.map(field => String(field.id || "").match(new RegExp(`^${prefix}(\\d+)(.+)$`))).filter(Boolean)
		const sourceNumber = Math.max(0, ...matches.map(match => Number(match?.[1] || 0)))
		const sourceFields = fields.filter(field => String(field.id || "").startsWith(`${prefix}${sourceNumber}`))
		const nextNumber = sourceNumber + 1
		const additions = sourceFields.length ? sourceFields.map(field => ({...field, id: String(field.id).replace(`${prefix}${sourceNumber}`, `${prefix}${nextNumber}`), title: String(field.title || "").replace(new RegExp(`^${sourceNumber}\\.`), `${nextNumber}.`)})) : [{id: `${prefix}${nextNumber}${group.suffix}`, section: String(group.section || ""), title: String(group.title || "New entry"), type: "MULTILINE"}]
		return {...config, fields: [...fields, ...additions]}
	}

	removeFormField(config: ToolConfig = {}, fieldId: string): ToolConfig {
		const fields = ((Array.isArray(config.fields) ? config.fields : []) as ToolConfig[]).filter(field => field.id !== fieldId).map(field => ({
			...field,
			showWhen: (field.showWhen as ToolConfig)?.fieldId === fieldId ? undefined : field.showWhen,
			specialValues: Array.isArray(field.specialValues) ? (field.specialValues as ToolConfig[]).map(option => option.requiresFieldId === fieldId ? {...option, requiresFieldId: ""} : option) : field.specialValues,
		}))
		return {...config, fields, focusFieldId: config.focusFieldId === fieldId ? "" : config.focusFieldId, headlineFieldId: config.headlineFieldId === fieldId ? "" : config.headlineFieldId}
	}

	moveFormConfigItem(config: ToolConfig = {}, listName: "sections" | "fields" | "minimumGroups", index: number, offset: number): ToolConfig {
		const items = (Array.isArray(config[listName]) ? [...config[listName] as ToolConfig[]] : [])
		const destination = index + offset
		if (index < 0 || destination < 0 || index >= items.length || destination >= items.length) return config
		items.splice(destination, 0, items.splice(index, 1)[0])
		return {...config, [listName]: items}
	}

	setFormRatingMaximum(config: ToolConfig = {}, maximum: number, fieldIds?: string[]): ToolConfig {
		const fields = ((Array.isArray(config.fields) ? config.fields : []) as ToolConfig[]).map(field => field.type === "SCALE" && (!fieldIds || fieldIds.includes(String(field.id))) ? {...field, max: maximum} : field)
		return {...config, ratingScaleMax: maximum, fields}
	}

	getFormSections(config: ToolConfig = {}): ToolConfig[] {
		const configured = Array.isArray(config.sections) ? config.sections as ToolConfig[] : []
		const sourceSections: ToolConfig[] = configured.length ? configured : [{id: "default", title: String(config.title || "Questions")}]
		const sections = sourceSections.map(section => ({...section, id: String(section.id || ""), fields: [] as ToolConfig[]}))
		for (const field of (Array.isArray(config.fields) ? config.fields : []) as ToolConfig[]) {
			const section = sections.find(item => item.id === field.section) || sections[0]
			section.fields.push(field)
		}
		return sections.filter(section => section.fields.length)
	}

	getStructuredFormView(config: ToolConfig = {}, answers: ToolConfig = {}): ToolConfig {
		return getStructuredFormView(config, answers)
	}

	createDefaultPayload(config: ToolConfig = {}): EnginePayload {
		if (config.handoutConfig) return {form: JSON.parse(JSON.stringify(this.normalizeConfig(config))), answers: {}, selectedFocusId: (config.handoutConfig as ToolConfig).defaultFocusId || "", acknowledged: false, acknowledgedAt: ""}
		return {form: this.normalizeConfig(config), answers: {}, ...(config.pairedQuestionnaire ? {reports: {}} : {})}
	}

	normalizePayload(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		if (config.scriptConfig) {
			const normalized: EnginePayload = {...payload, form: payload.form || this.normalizeConfig(config), answers: payload.answers === undefined ? {} : payload.answers}
			if (normalized.readySignature && !getFormScriptView(config, normalized).isReady) normalized.readySignature = ""
			return normalized
		}
		if (config.ordinalProfile) return {...payload, archivedItems: getArchivedOrdinalProfileItems(payload, config), form: JSON.parse(JSON.stringify(this.normalizeConfig(config))), answers: payload.answers === undefined ? {} : payload.answers}
		if (config.subscaleProfile) {
			const form = JSON.parse(JSON.stringify(this.normalizeConfig(config)))
			form.subscaleProfile.archivedItems = (((payload.form as ToolConfig)?.subscaleProfile || {}) as ToolConfig).archivedItems || []
			return {...payload, form, answers: payload.answers === undefined ? {} : payload.answers}
		}
		if (config.pairedQuestionnaire) return {...payload, form: JSON.parse(JSON.stringify(this.normalizeConfig(config))), reports: payload.reports === undefined ? {} : payload.reports, answers: payload.answers === undefined ? {} : payload.answers}
		return {...payload, form: payload.form || this.normalizeConfig(config), answers: payload.answers === undefined ? {} : payload.answers}
	}

	validateConfig(config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = getFormCapabilityConfigErrors(config)
		result.isValid = result.errors.length === 0
		return result
	}

	validateFormPayload(payload: EnginePayload = {}, config: ToolConfig = {}): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = getFormCapabilityPayloadErrors(payload, config)
		result.isValid = result.errors.length === 0
		return result
	}

	createSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		if (config.pairedQuestionnaire) {
			const report = getPairedQuestionnaireReport(config, payload.reports as ToolConfig)
			const partners = Array.isArray(report.partners) ? report.partners as ToolConfig[] : []
			return {headline: partners.map(partner => `${partner.title}: ${partner.answeredCount}/${partner.itemCount}`).join(" · "), form: payload.form || config, reports: payload.reports || {}, questionnaireReport: report}
		}
		if (config.handoutConfig) return getHandoutSummary(payload, config)
		if (config.anchoredMean) {
			const anchoredMean = config.anchoredMean as ToolConfig
			const report = getAnchoredMeanReport(config, payload.answers || {})
			return {...createFormSummaryInput(payload, config), headline: report.mean === null ? String(anchoredMean.incompleteLabel || "Incomplete") : `${anchoredMean.scoreLabel}: ${report.mean}`, scoreResult: report.mean, anchoredMeanReport: report}
		}
		if (config.scriptConfig) return createFormScriptSummary(payload, config)
		if (config.ordinalProfile) return createOrdinalProfileSummary(payload, config)
		if (config.subscaleProfile) {
			const report = getSubscaleProfileResult((payload.answers || {}) as ToolConfig, config)
			return {headline: `${report.answeredCount}/${report.itemCount} answered`, form: payload.form || config, answers: payload.answers || {}, profileResult: report}
		}
		if (config.frequencyInventory) {
			const normalized = this.normalizePayload(payload, config)
			const inventory = config.frequencyInventory as ToolConfig
			const report = getFrequencyInventoryReport(inventory, normalized.answers as ToolConfig)
			return {headline: `${inventory.sourceLabel}: ${report.answeredCount}/${report.questionCount} ${inventory.answeredLabel}`, form: normalized.form, answers: normalized.answers, frequencyReport: report, completedDate: payload.completedDate || new Date().toISOString().slice(0, 10)}
		}
		return createFormSummaryInput(payload, config)
	}

	createSubscaleProfileSubmission(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		const validation = this.validateFormPayload(payload, config)
		if (!validation.isValid) throw new Error(validation.errors[0])
		const normalized = this.normalizePayload(payload, config)
		return {...normalized, profileResult: getSubscaleProfileResult((normalized.answers || {}) as ToolConfig, config), scoreResult: null}
	}

	createScoredSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}): EnginePayload {
		const summary = this.createSummaryInput(payload, config)
		return {...summary, headline: summary.scoreResult ?? summary.headline}
	}

	createFormItemFromToolConfig(config: ToolConfig = {}, options: ToolEngineOptions = new ToolEngineOptions()): FormModelInterface {
		let formItem = plainToClass(FormItem, {
			...config,
			formElements: config.fields || config.formElements || [],
		})
		formItem.title = formItem.title || String(config.title || "")
		if (options.syncGlobalScaleConfig !== false) {
			formItem.syncScaleElementsWithGlobalScaleConfig()
		}
		return formItem
	}

	createToolConfigFromFormItem(formItem: FormModelInterface | null = null): ToolConfig {
		let result: ToolConfig = {}
		if (formItem) {
			result = formItem.asJsonObject()
			result.fields = result.formElements || []
			result = this.normalizeConfig(result)
			delete result.formElements
		}
		return result
	}

	createFormEnginePayload(enginePayload: EnginePayload = {}, formItem: FormModelInterface | null = null): EnginePayload {
		const form = this.createToolConfigFromFormItem(formItem)
		const answers = this.createAnswerMap(formItem)
		const payload = {
			...(enginePayload || {}),
			form,
			answers,
			scoreResult: formItem?.getScoreResultsOrRecalculate?.(),
		}
		return this.normalizePayload(payload, form)
	}

	updateFormAnswer(payload: EnginePayload = {}, config: ToolConfig = {}, fieldId: string, answer: string | number | null): EnginePayload {
		const form = this.normalizeConfig(config)
		const fields = (form.fields || []) as ToolConfig[]
		const frequencyQuestions = (((config.frequencyInventory as ToolConfig)?.questions || []) as ToolConfig[])
		const profileItems = (((config.subscaleProfile as ToolConfig)?.items || []) as ToolConfig[])
		if (!fields.some(field => field.id === fieldId) && !frequencyQuestions.some(question => question.id === fieldId) && !profileItems.some(item => item.id === fieldId)) throw new Error(`Unknown form field: ${fieldId}`)
		const answers = {...((payload.answers || {}) as ToolConfig), [fieldId]: answer}
		if (answer === null && (config.frequencyInventory || config.subscaleProfile)) delete answers[fieldId]
		const updated: EnginePayload = {...payload, form: config.ordinalProfile || config.subscaleProfile ? payload.form : form, answers, scoreResult: null}
		if (config.subscaleProfile) delete updated.profileResult
		return this.normalizePayload(updated, form)
	}

	createAnswerMap(formItem: FormModelInterface | null = null): ToolConfig {
		let answers: ToolConfig = {}
		for (let formElement of formItem?.formElements || []) {
			answers[formElement.id] = formElement.getAnswer()
		}
		return answers
	}
}
